import { Organization } from '@/account/api/types';
import { CRMDialog } from '@/account/components/crm-dialog';
import { HubSpotCRMRow } from '@/account/components/hubspot-crm-row';
import { NetSuiteCRMRow } from '@/account/components/netsuite-crm-row';
import { QuickBooksConnectionDialog } from '@/account/components/quickbooks-connection-dialog';
import { QuickBooksCRMRow } from '@/account/components/quickbooks-crm-row';
import { SalesforceConnectionDialog } from '@/account/components/salesforce-connection-dialog';
import { SalesforceCRMRow } from '@/account/components/salesforce-crm-row';
import { useDisclosure } from '@/hooks/use-disclosure';
import { findIntegrationConfig, hasIntegration } from '@/utilities/integration';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';

interface CRMListProps {
  organization: Organization;
}

export function CRMList(props: CRMListProps) {
  const integrationDialog = useDisclosure();
  const salesforceDialog = useDisclosure();
  const quickbooksDialog = useDisclosure();
  const [defaultTab, setDefaultTab] = useState<number | undefined>(undefined);

  // CRM connection states (HubSpot and Salesforce are mutually exclusive)
  const hubspotConfig = findIntegrationConfig(
    props.organization.integrationConfigurations,
    'HUBSPOT'
  );
  const sfdcConfig = findIntegrationConfig(
    props.organization.integrationConfigurations,
    'SFDC'
  );
  const isHubspotConnected = hubspotConfig !== null;
  const isSalesforceConnected =
    sfdcConfig !== null && sfdcConfig.configurationState.connected === true;

  // NetSuite connection state (QuickBooks and NetSuite are mutually exclusive)
  const isNetSuiteConnected = hasIntegration(
    props.organization.enabledIntegrations,
    'NETSUITE'
  );

  return (
    <>
      <CRMDialog
        isOpen={integrationDialog.isOpen}
        organization={props.organization}
        onClose={integrationDialog.close}
        defaultTab={defaultTab}
      />
      <SalesforceConnectionDialog
        organization={props.organization}
        isOpen={salesforceDialog.isOpen}
        onClose={salesforceDialog.close}
      />
      <QuickBooksConnectionDialog
        isOpen={quickbooksDialog.isOpen}
        organization={props.organization}
        onClose={quickbooksDialog.close}
      />

      {/* CRM Integrations Table */}
      <Box sx={{ m: 2, marginTop: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Sales and customer relationship management (CRM)
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap', width: 180 }}>
                  Integration
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Connected</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Configured</TableCell>
                <TableCell>Syncing</TableCell>
                <TableCell sx={{ width: 48 }} align={'right'}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <HubSpotCRMRow
                organization={props.organization}
                isSalesforceConnected={isSalesforceConnected}
                isEnabledForOrg={hasIntegration(
                  props.organization.availableIntegrations,
                  'HUBSPOT'
                )}
                onOpenIntegrationDialog={() => {
                  setDefaultTab(0);
                  integrationDialog.open();
                }}
              />
              <SalesforceCRMRow
                organization={props.organization}
                isHubSpotConnected={isHubspotConnected}
                isEnabledForOrg={hasIntegration(
                  props.organization.availableIntegrations,
                  'SFDC'
                )}
                onOpenSalesforceDialog={salesforceDialog.open}
              />
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Accounting Integrations Table */}
      <Box sx={{ m: 2, marginTop: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Accounting and enterprise resource planning (ERP)
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap', width: 180 }}>
                  Integration
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Connected</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Configured</TableCell>
                <TableCell>Syncing</TableCell>
                <TableCell sx={{ width: 48 }} align={'right'}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <QuickBooksCRMRow
                organization={props.organization}
                isNetSuiteConnected={isNetSuiteConnected}
                isEnabledForOrg={hasIntegration(
                  props.organization.availableIntegrations,
                  'QBO'
                )}
                onOpenConnectionDialog={quickbooksDialog.open}
              />
              <NetSuiteCRMRow
                isEnabledForOrg={hasIntegration(
                  props.organization.availableIntegrations,
                  'NETSUITE'
                )}
                isQuickBooksConnected={
                  findIntegrationConfig(
                    props.organization.integrationConfigurations,
                    'QBO'
                  ) !== null
                }
              />
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
}
